import React, { useEffect, useMemo, useState } from 'react';
import { useNotificationsStore } from '@/stores/notifications';
import { useUiStore } from '@/stores/ui';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Notification } from '@/core/state-shape';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MailCheck, Mail, Bell, Check, MailOpen, MoreVertical, Archive, Clock, ArchiveIcon } from 'lucide-react';

const getNotificationIcon = (kind: Notification['kind']) => {
    switch(kind) {
        case 'task': return <Bell className="h-4 w-4 text-muted-foreground" />;
        case 'validator': return <Check className="h-4 w-4 text-muted-foreground" />;
        case 'review': return <MailOpen className="h-4 w-4 text-muted-foreground" />;
        case 'error': return <ArchiveIcon className="h-4 w-4 text-destructive" />;
        default: return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
}

const NotificationItem = ({ notification, onMarkRead, onArchive, onSnooze }: {
    notification: Notification, 
    onMarkRead: (ids: string[]) => void,
    onArchive: (ids: string[]) => void,
    onSnooze: (ids: string[], until: string) => void 
}) => {
    const isRead = !!notification.readAt;

    const handleSnooze = (duration: 'hour' | 'tomorrow') => {
        const now = new Date();
        if (duration === 'hour') {
            now.setHours(now.getHours() + 1);
        } else { // tomorrow 9am
            now.setDate(now.getDate() + 1);
            now.setHours(9, 0, 0, 0);
        }
        onSnooze([notification.id], now.toISOString());
    };

    return (
        <Card className={isRead ? 'bg-muted/30' : 'bg-card'}>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    {getNotificationIcon(notification.kind)}
                    {notification.title}
                </CardTitle>
                <CardDescription className="pt-1">
                    {new Date(notification.createdAt).toLocaleString()}
                </CardDescription>
            </CardHeader>
            {notification.body && (
                <CardContent><p className="text-sm text-muted-foreground">{notification.body}</p></CardContent>
            )}
            <CardFooter className="flex justify-between items-center bg-muted/20">
                <Badge variant="outline" className="capitalize">{notification.kind}</Badge>
                <div className="flex items-center gap-1">
                    {!isRead && (
                        <Button variant="outline" size="sm" onClick={() => onMarkRead([notification.id])}>
                            <MailCheck className="mr-2 h-4 w-4" />
                            Mark as Read
                        </Button>
                    )}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleSnooze('hour')}>
                                <Clock className="mr-2 h-4 w-4" /> Snooze for 1 hour
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onArchive([notification.id])}>
                                <Archive className="mr-2 h-4 w-4" /> Archive
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </CardFooter>
        </Card>
    );
};


export default function InboxPage() {
    const { order, byId, unreadCount, fetchAll, markRead, archive, snooze } = useNotificationsStore();
    const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);
    const [filter, setFilter] = useState<'all' | 'unread'>('unread');

    useEffect(() => {
        setHeaderTitle('Inbox');
    }, [setHeaderTitle]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const handleMarkAllRead = () => {
        const unreadIds = order.filter(id => byId[id] && !byId[id].readAt);
        if (unreadIds.length > 0) {
            markRead(unreadIds);
        }
    };

    const notifications = useMemo(() => {
        const now = new Date().getTime();
        const all = order.map(id => byId[id]).filter(Boolean) as Notification[];

        const nonArchived = all.filter(n => !n.archived);

        if (filter === 'unread') {
            return nonArchived.filter(n => {
                const isSnoozed = n.snoozeUntil && new Date(n.snoozeUntil).getTime() > now;
                return !n.readAt && !isSnoozed;
            });
        }
        return nonArchived;
    }, [order, byId, filter]);
    return (
        <div className="container mx-auto p-4 lg:p-6 space-y-6">
            <header className="flex justify-between items-center flex-wrap gap-4">
                <div className="hidden md:block">
                    <h1 className="text-2xl font-bold ">Inbox</h1>
                    <p className="text-muted-foreground">You have {unreadCount} unread notifications.</p>
                </div>
                <Button variant="outline" onClick={handleMarkAllRead} disabled={unreadCount === 0}>

                    <MailCheck className="mr-2 h-4 w-4" />
                    Mark all as read
                </Button>
            </header>

            <Tabs value={filter} onValueChange={(value) => setFilter(value as any)} className="w-full">
                <TabsList>
                    <TabsTrigger value="unread"><Mail className="mr-2 h-4 w-4" /> Unread</TabsTrigger>
                    <TabsTrigger value="all"><MailOpen className="mr-2 h-4 w-4" /> All</TabsTrigger>
                </TabsList>
            </Tabs>

            {notifications.length > 0 ? (
                <div className="space-y-4">
                    {notifications.map(notification => (
                        notification && <NotificationItem 
                            key={notification.id} 
                            notification={notification} 
                            onMarkRead={markRead}
                            onArchive={archive}
                            onSnooze={snooze}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-16 text-muted-foreground rounded-lg border border-dashed">
                    <h2 className="text-xl font-semibold">
                        {filter === 'unread' ? "You're all caught up! 🎉" : `No ${filter} notifications`}
                    </h2>
                    <p className="mt-2">
                        {filter === 'unread' ? "There are no new notifications." : "Your inbox is empty."}
                    </p>
                </div>
            )}
        </div>
    );
}